import { useEffect, useRef, useState } from "react";
import type { TerraDraw as TerraDrawInstance, GeoJSONStoreFeatures } from "terra-draw";
import { OdsTerraDrawGoogleMapsAdapter, selectRoofVertexForDrag } from "./googleTerraDrawAdapter";
import { Button } from "@/components/ui/button";
import { geodesicAreaM2, validateRoofSurvey, type RoofFeature } from "../../../../shared/commercial/roofGeometry";

function toTerraDrawRoofFeature(feature: RoofFeature): GeoJSONStoreFeatures {
  // Terra Draw routes addFeatures() through its instantiated mode validators. The
  // canonical persisted schema intentionally has no renderer-specific mode field.
  const coordinates = feature.geometry.coordinates.map(ring => ring.map(([longitude, latitude]) => [Number(longitude.toFixed(9)), Number(latitude.toFixed(9))] as [number, number]));
  return { ...feature, geometry: { ...feature.geometry, coordinates }, properties: { ...feature.properties, mode: "polygon" } } as unknown as GeoJSONStoreFeatures;
}

function addPersistedRoofFeatures(draw: TerraDrawInstance, features: RoofFeature[]) {
  if (!features.length) return;
  const expectedIds = new Set(features.map(feature => String(feature.id)));
  const validation = draw.addFeatures(features.map(toTerraDrawRoofFeature));
  const acceptedIds = new Set(draw.getSnapshot().map(feature => String(feature.id)));
  const rejected = validation.filter(result => result.valid !== true || !expectedIds.has(String(result.id)));
  const missing = Array.from(expectedIds).filter(id => !acceptedIds.has(id));
  if (rejected.length || missing.length || acceptedIds.size < expectedIds.size) {
    throw new Error("Terra Draw rejected one or more saved roof features during hydration: " + rejected.map(result => String(result.reason ?? "invalid feature")).join("; ") + (missing.length ? "; missing " + missing.length + " feature(s)" : ""));
  }
}

function safeDiagnostic(error: unknown) {
  const name = error instanceof Error ? error.name : "UnknownError";
  const raw = error instanceof Error ? error.message : String(error);
  const message = raw
    .replace(/AIza[0-9A-Za-z_-]{20,}/g, "[redacted]")
    .replace(/https?:\/\/[^\s)]+/g, "[redacted-url]")
    .replace(/((?:key|token|secret)=)[^&\s]+/gi, "$1[redacted]")
    .slice(0, 180);
  return name + ": " + message;
}

function reportLifecycleFailure(phase: string, error: unknown) {
  console.warn("[GoogleRoofDraw] " + phase + " failed: " + safeDiagnostic(error));
}
export function GoogleRoofDraw({ map, features, disabled, onFeaturesChange, onError }: {
  map: google.maps.Map; features: RoofFeature[]; disabled: boolean; onFeaturesChange: (features: RoofFeature[]) => void; onError: (message: string) => void;
}) {
  const drawRef = useRef<TerraDrawInstance | null>(null);
  const synchronizing = useRef(false);
  const lastPublishedFeatures = useRef<RoofFeature[] | null>(null);
  const latest = useRef({ features, disabled, onFeaturesChange, onError }); latest.current = { features, disabled, onFeaturesChange, onError };
  const [mode, setMode] = useState<"select" | "polygon" | "rectangle">("select");
  const [error, setError] = useState("");
  const gesturePaneRef = useRef<HTMLElement | null>(null);
  const gesturePaneOriginalPointerEvents = useRef(new WeakMap<HTMLElement, string>());
  function updateGesturePaneHitTesting(editing: boolean) {
    const gesturePane = gesturePaneRef.current;
    if (!gesturePane) return;
    if (!gesturePaneOriginalPointerEvents.current.has(gesturePane))
      gesturePaneOriginalPointerEvents.current.set(gesturePane, gesturePane.style.pointerEvents);
    gesturePane.style.pointerEvents = editing ? "none" : gesturePaneOriginalPointerEvents.current.get(gesturePane) ?? "";
  }
  useEffect(() => {
    let alive = true;
    let draw: TerraDrawInstance | null = null;
    lastPublishedFeatures.current = null;
    const initialMode = mode;
    void import("terra-draw").then(async terra => {
      if (!alive) return;
      const adapter: OdsTerraDrawGoogleMapsAdapter = new OdsTerraDrawGoogleMapsAdapter({ map, lib: google.maps, eventRoot: map.getDiv(),
        prepareVertexDrag: event => !latest.current.disabled && !!draw && selectRoofVertexForDrag(draw, event, adapter),
      });
      gesturePaneRef.current = adapter.getInteractionPane();
      draw = new terra.TerraDraw({
        adapter,
        idStrategy: { isValidId: (id: string | number) => typeof id === "string" && id.length > 0, getId: () => crypto.randomUUID() },
        modes: [
          new terra.TerraDrawPolygonMode({ editable: true, showCoordinatePoints: true, styles: { fillColor: "#FF4D14", fillOpacity: 0.18, outlineColor: "#FF4D14", outlineWidth: 3 } }),
          new terra.TerraDrawRectangleMode({ styles: { fillColor: "#FF4D14", fillOpacity: 0.18, outlineColor: "#FF4D14", outlineWidth: 3 } }),
          new terra.TerraDrawSelectMode({ dragEventThrottle: 0, flags: { polygon: { feature: { draggable: true, coordinates: { draggable: true, midpoints: { draggable: true }, deletable: true } } } } }),
        ],
      });
      const instance = draw;
      drawRef.current = instance; instance.start(); addPersistedRoofFeatures(instance, latest.current.features); instance.setMode(latest.current.disabled ? "select" : initialMode);
      updateGesturePaneHitTesting(!latest.current.disabled && initialMode === "select");
      map.setOptions(latest.current.disabled || initialMode === "select"
        ? { draggable: true, gestureHandling: "greedy" }
        : { draggable: false, gestureHandling: "none" });
      const publishCompleted = () => {
        if (!alive) return;
        // Terra Draw emits change during every pointer step. Its active polygon can
        // be a LineString (polygon mode) or a provisional Polygon (rectangle mode).
        // Keep those live in the same instance; only validate closed features.
        const completed = instance.getSnapshot().filter((raw: GeoJSONStoreFeatures) =>
          raw.geometry.type === "Polygon" && raw.geometry.coordinates.length === 1 && raw.properties?.currentlyDrawing !== true
        );
        if (!completed.length) { const empty: RoofFeature[] = []; lastPublishedFeatures.current = empty; latest.current.onFeaturesChange(empty); setError(""); return; }
        const next: RoofFeature[] = completed.map((raw, index) => {
          const coordinates = raw.geometry.coordinates as number[][][];
          const previous = latest.current.features.find(feature => feature.id === String(raw.id));
          const runtimeRing = coordinates[0].map((point: number[]) => [Number(point[0]), Number(point[1])] as [number, number]);
          const canonicalPrevious = previous && JSON.stringify(toTerraDrawRoofFeature(previous).geometry) === JSON.stringify(raw.geometry) ? previous : undefined;
          const ring = canonicalPrevious ? canonicalPrevious.geometry.coordinates[0] : runtimeRing;
          return { type: "Feature" as const, id: String(raw.id), properties: { name: previous?.properties.name ?? `Zona ${index + 1}`, areaM2: geodesicAreaM2(ring), orientation: previous?.properties.orientation ?? "unknown" as const, tiltDegrees: previous?.properties.tiltDegrees ?? null, reviewStatus: previous?.properties.reviewStatus ?? "pending" as const }, geometry: { type: "Polygon" as const, coordinates: [ring] } };
        });
        try {
          const validated = validateRoofSurvey({ type: "FeatureCollection", mode: "drawn", source: "google_maps", features: next }).features;
          lastPublishedFeatures.current = validated;
          latest.current.onFeaturesChange(validated);
          setError("");
        } catch (caught) {
          const message = String((caught as Error)?.message ?? "");
          const friendly = message.includes("ROOF_ZONES_OVERLAP") ? "Las cubiertas se solapan; separa sus interiores. Los bordes compartidos están permitidos." : "El contorno debe ser una superficie cerrada y válida.";
          setError(friendly); latest.current.onError(friendly);
          // Do not clear: the invalid finished geometry remains on the canvas so
          // the user can edit it and retry; prior committed state is untouched.
        }
      };
      instance.on("change", (_ids, type) => {
        // Provisional is emitted for each pointer movement while a ring is open.
        // Commit is relevant only when editing existing, already-finished geometry.
        if (type === "delete") { if (!synchronizing.current) publishCompleted(); return; }
        if (type !== "commit") return;
        const snapshot = instance.getSnapshot();
        const hasActiveDraft = snapshot.some(feature => feature.properties?.currentlyDrawing === true || feature.geometry.type === "LineString");
        if (!hasActiveDraft) publishCompleted();
      });
      instance.on("finish", () => publishCompleted());

    }).catch(error => {
      reportLifecycleFailure("initialize", error);
      const failedDraw = draw; draw = null;
      if (drawRef.current === failedDraw) drawRef.current = null;
      try { failedDraw?.stop(); } catch (disposeError) { reportLifecycleFailure("dispose-after-initialize-failure", disposeError); }
      updateGesturePaneHitTesting(false); gesturePaneRef.current = null;
      if (alive) { setError("No se pudo inicializar el editor de cubiertas de Google."); latest.current.onError("No se pudo inicializar el editor de cubiertas de Google."); }
    });
    return () => {
      alive = false;
      const stoppedDraw = draw; draw = null;
      if (drawRef.current === stoppedDraw) drawRef.current = null;
      try { stoppedDraw?.stop(); } catch (error) { reportLifecycleFailure("dispose", error); }
      updateGesturePaneHitTesting(false);
      gesturePaneRef.current = null;
      map.setOptions({ draggable: true, gestureHandling: "greedy" });
    };
  }, [map]);
  useEffect(() => {
    const draw = drawRef.current; if (!draw) return;
    const live = JSON.stringify(draw.getSnapshot().filter(feature => feature.geometry.type === "Polygon" && feature.properties?.currentlyDrawing !== true).map(feature => ({ id: String(feature.id), geometry: feature.geometry })));
    const wanted = JSON.stringify(features.map(feature => ({ id: feature.id, geometry: toTerraDrawRoofFeature(feature).geometry })));
    // An insertion finish can publish before the remaining drag movements.
    // React echoes that committed snapshot while Terra Draw holds newer
    // provisional coordinates: it is not an external replacement of the roof.
    if (live !== wanted && features !== lastPublishedFeatures.current) {
      lastPublishedFeatures.current = null;
      const previous = draw.getSnapshot();
      synchronizing.current = true;
      try {
        draw.clear();
        addPersistedRoofFeatures(draw, features);
        setError("");
      } catch (error) {
        reportLifecycleFailure("synchronize", error);
        try {
          draw.clear();
          const restored = draw.addFeatures(previous);
          if (restored.some(result => result.valid !== true)) reportLifecycleFailure("restore-previous-snapshot", new Error("Terra Draw rejected its prior snapshot."));
        } catch (restoreError) { reportLifecycleFailure("restore-previous-snapshot", restoreError); }
        const message = "No se pudieron sincronizar las cubiertas guardadas en el editor.";
        setError(message); latest.current.onError(message);
      } finally { synchronizing.current = false; }
    }
  }, [features, map]);
  useEffect(() => {
    const draw = drawRef.current; if (!draw) return;
    // setMode is a stop/start operation even when the name is unchanged. A
    // feature echo during drag must not deselect or reset the active gesture.
    const requestedMode = disabled ? "select" : mode;
    if (draw.getMode() !== requestedMode) draw.setMode(requestedMode);
    updateGesturePaneHitTesting(!disabled && mode === "select");
    map.setOptions(mode === "select" || disabled
      ? { draggable: true, gestureHandling: "greedy" }
      : { draggable: false, gestureHandling: "none" });
  }, [disabled, mode, map]);
  return <div className="pointer-events-auto absolute inset-x-2 top-2 z-10 flex flex-wrap gap-2 rounded-xl bg-background/95 p-2 shadow">
    <Button type="button" size="touch" variant={mode === "select" ? "default" : "outline"} disabled={disabled} onClick={() => { setMode("select"); }}>Mover / editar</Button>
    <Button type="button" size="touch" variant={mode === "polygon" ? "default" : "outline"} disabled={disabled || features.length >= 30} onClick={() => { setMode("polygon"); }}>Dibujar esquinas</Button>
    <Button type="button" size="touch" variant={mode === "rectangle" ? "default" : "outline"} disabled={disabled || features.length >= 30} onClick={() => { setMode("rectangle"); }}>Rectángulo</Button>
    <Button type="button" size="touch" variant="outline" disabled={disabled || !drawRef.current?.canUndo()} onClick={() => drawRef.current?.undo()}>Deshacer</Button>
    <Button type="button" size="touch" variant="outline" disabled={disabled || !features.length} onClick={() => { const last = drawRef.current?.getSnapshot().at(-1); if (last?.id !== undefined) drawRef.current?.removeFeatures([last.id]); }}>Borrar última</Button>
    <span className="self-center text-xs">Cierra sobre el primer vértice/doble toque; en selección arrastra vértices. Dibujo bloquea el pan accidental.</span>
    {error && <span role="alert" className="w-full text-sm text-destructive">{error}</span>}
  </div>;
}
